/**
 * Client-side weather source classification for Admin Data Health.
 * Mirrors server/weather/services/weatherDataHealthCatalog.js for resilient filtering.
 */

import { WEATHER_SOURCE_CODES } from "../constants/weatherSourceCodes";
import { isTransportDataHealthSource } from "../constants/transportSourceConstants";
import { isFoodDataHealthSource } from "../constants/foodSourceConstants";
import { isCultureDataHealthSource } from "../constants/cultureSourceConstants";

const WEATHER_PIPELINE_CODES = new Set([
  ...Object.values(WEATHER_SOURCE_CODES),
  "COMMUNITY_REPORTS",
  "OFFICIAL_WEATHER_ALERTS",
  "NASA_GPM_IMERG",
  "SIATA_LOCAL_OBSERVATIONS",
]);

const WEATHER_PIPELINE_PREFIXES = [
  "MIDPOINT_SAMPLE_",
  "MIDPOINT_STORM_",
  "MIDPOINT_WEATHER_",
];

const WEATHER_DATA_SOURCE_CODES = new Set(["mock_weather_alerts"]);

export function isWeatherDataHealthSource(sourceCode) {
  const normalized = String(sourceCode || "").trim();
  if (!normalized) {
    return false;
  }

  const upper = normalized.toUpperCase();
  if (WEATHER_PIPELINE_CODES.has(upper)) {
    return true;
  }

  if (WEATHER_DATA_SOURCE_CODES.has(normalized.toLowerCase())) {
    return true;
  }

  if (normalized.toLowerCase().startsWith("siata-")) {
    return true;
  }

  return WEATHER_PIPELINE_PREFIXES.some((prefix) => upper.startsWith(prefix));
}

export function resolveDataHealthSourceDomain(source) {
  if (source?.sourceDomain) {
    return source.sourceDomain;
  }

  if (isWeatherDataHealthSource(source?.sourceCode)) {
    return "weather";
  }

  if (isTransportDataHealthSource(source?.sourceCode)) {
    return "transport";
  }

  if (isFoodDataHealthSource(source?.sourceCode)) {
    return "food";
  }

  if (isCultureDataHealthSource(source?.sourceCode)) {
    return "culture";
  }

  if (String(source?.sourceType || "").toLowerCase() === "broker_website") {
    return "real_estate";
  }

  return "ingestion";
}

export const WEATHER_SOURCE_GROUP_LABELS = {
  pipeline: "Weather pipeline",
  siata_etl: "SIATA ETL",
  registry: "Registry",
  ingestion: "Legacy ingestion",
};

export function weatherSourceGroupLabel(sourceGroup) {
  return WEATHER_SOURCE_GROUP_LABELS[sourceGroup] || null;
}
